import React, { useRef, useState } from 'react';
import { Camera, Loader2, X } from 'lucide-react';
import { reviewsAPI, uploadsAPI } from '../services/api';

const ACCEPT = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_SIZE = 5 * 1024 * 1024;
export const MAX_PHOTOS = 3;

const ReviewPhotoUpload = ({ photos, onChange }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(0);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const remaining = MAX_PHOTOS - photos.length;

  const handleFiles = async (fileList) => {
    setError('');
    const files = Array.from(fileList || []).slice(0, remaining);
    if (!files.length) { if (fileList?.length) setError(`${MAX_PHOTOS} photos maximum`); return; }
    const bad = files.find(f => !ACCEPT.includes(f.type) || f.size > MAX_SIZE);
    if (bad) { setError(!ACCEPT.includes(bad.type) ? 'Format non supporté (JPG, PNG ou WebP)' : 'Photo trop volumineuse (5 Mo maximum)'); return; }
    setUploading(files.length);
    const urls = [];
    try {
      for (const file of files) {
        setProgress(0);
        const res = await reviewsAPI.uploadPhoto(file, setProgress);
        urls.push(res.url);
      }
      onChange([...photos, ...urls]);
    } catch (e) {
      if (urls.length) onChange([...photos, ...urls]);
      setError(e.response?.data?.detail || "Échec de l'envoi de la photo");
    } finally {
      setUploading(0);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div data-testid="review-photo-upload">
      <p className="text-xs font-medium text-gray-600 mb-1">Photos <span className="font-normal text-gray-400">(facultatif · {photos.length}/{MAX_PHOTOS})</span></p>
      <div className="flex flex-wrap gap-2">
        {photos.map((url, i) => (
          <div key={url} className="relative w-20 h-20 rounded-md border border-gray-200 bg-white overflow-hidden group" data-testid={`review-photo-thumb-${i}`}>
            <img src={uploadsAPI.resolveUrl(url)} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
            <button type="button" data-testid={`review-photo-remove-${i}`} onClick={() => onChange(photos.filter(p => p !== url))} title="Retirer"
              className="absolute top-1 right-1 bg-black/60 hover:bg-red-600 text-white rounded-full p-0.5 transition-colors"><X size={12} /></button>
          </div>
        ))}
        {remaining > 0 && (
          <div
            data-testid="review-photo-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); if (!uploading) handleFiles(e.dataTransfer.files); }}
            onClick={() => !uploading && inputRef.current?.click()}
            className="w-20 h-20 rounded-md border-2 border-dashed border-gray-300 hover:border-orange-400 hover:bg-orange-50/40 flex flex-col items-center justify-center text-gray-500 text-[11px] cursor-pointer transition-colors"
          >
            {uploading ? <><Loader2 size={18} className="animate-spin text-orange-500" /><span className="mt-1">{progress}%</span></> : <><Camera size={18} className="text-orange-500" /><span className="mt-1">Ajouter</span></>}
            <input ref={inputRef} data-testid="review-photo-input" type="file" accept={ACCEPT.join(',')} multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
          </div>
        )}
      </div>
      <p className="text-[11px] text-gray-400 mt-1">JPG, PNG ou WebP · 5 Mo max · montrez la pièce installée sur votre véhicule</p>
      {error && <p className="text-xs text-red-600 mt-1" data-testid="review-photo-error">{error}</p>}
    </div>
  );
};

export default ReviewPhotoUpload;
